import { addWeeks, format, isBefore } from 'date-fns';
import { toDate } from '../../../shared/utils/dateHelpers';
import { SUCCESS_UPDATE_INTERVAL_DAYS } from '../../../shared/success/successRecords';
import { foundationAlerts, ignitionAlertReasons, ignitionPendingReports, ReportFlags, Submission } from './alertRules';
import { currentProgramme, firstSessionDate, LoadedClient, programmePtTotal, RawClient } from './clientRecord';
import { ClientTask, ClientWork, Programme, TaskKind, Urgency } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Check-ins only apply to clients whose first session was after this date (1 April 2025). */
export const CHECK_IN_CUTOFF = new Date(2025, 3, 1);

/**
 * What is known about one client beyond its own document. Each field is
 * undefined while its source loads; the rules that need it are skipped, so a
 * slow or failed source hides tasks instead of inventing them.
 */
export interface TaskInputs {
  completedCheckIns?: Set<string>;
  latestSuccess?: Map<string, Date>;
  /** Cleared reasons for this client. `null` means the cleared lists failed to load: alerts then show uncleared. */
  cleared?: { ignition: Set<string>; foundation: Set<string> } | null;
  reports?: ReportFlags;
  submissions?: Submission[];
}

const fmtDay = (date: Date) => format(date, 'd MMM');

type Described = Pick<ClientTask, 'kind' | 'title' | 'detail' | 'urgency' | 'dueAt'>;

const SERVICE_LABELS: Record<string, string> = {
  nutrition: 'a nutrition session',
  metabolic: 'a metabolic screening',
  'performance screening': 'a performance screening',
  'allied health': 'an allied health session',
  'physical performance (Vald)': 'a Vald performance session',
};

const ASSESSMENT_NAMES: Record<string, string> = {
  Nutrition: 'nutrition',
  Metabolic: 'metabolic',
  'Physical Performance': 'physical performance',
  InBody: 'InBody',
  Inbody: 'InBody',
  Movement: 'movement',
  'Allied Health': 'allied health',
  Training: 'training',
};

/** Turns a stored alert reason into a task a person can act on, with when it fell due. */
export function describeAlertReason(reason: string, start: Date | null, end: Date | null, now: Date): Described {
  const stage = reason.match(/\[(At 2 weeks|Less than 6 weeks remaining|Less than 1 week remaining)\]$/)?.[1];
  const dueAt =
    stage === 'At 2 weeks' && start
      ? new Date(start.getTime() + 14 * DAY_MS)
      : stage === 'Less than 6 weeks remaining' && end
        ? new Date(end.getTime() - 42 * DAY_MS)
        : stage === 'Less than 1 week remaining' && end
          ? new Date(end.getTime() - 7 * DAY_MS)
          : null;
  const urgency: Urgency = stage === 'Less than 6 weeks remaining' ? 'soon' : 'overdue';
  const ending = end ? (end.getTime() < now.getTime() ? `programme ended ${fmtDay(end)}` : `programme ends ${fmtDay(end)}`) : 'programme ending';

  const sessions = reason.match(/^(\d+) (.+?) sessions? redeemed \[/);
  if (sessions && stage) {
    const [, count, service] = sessions;
    const detail =
      stage === 'At 2 weeks' ? 'None used by week 2' : stage === 'Less than 6 weeks remaining' ? `${count} used, under 6 weeks left` : `${count} used, ${ending}`;
    return { kind: 'session', title: `Book ${SERVICE_LABELS[service] ?? `a ${service} session`}`, detail, urgency, dueAt };
  }

  const report = reason.match(/^(Initial|Review) report not generated/);
  if (report) {
    const initial = report[1] === 'Initial';
    return {
      kind: 'report',
      title: initial ? 'Generate the initial report' : 'Generate the review report',
      detail: initial ? 'Due at week 2' : `Due before the ${ending}`,
      urgency: 'overdue',
      dueAt,
    };
  }

  if (reason === 'Training Plan Initial Submission Outstanding') {
    return { kind: 'assessment', title: 'Submit the initial training plan', detail: 'Not submitted yet', urgency: 'soon', dueAt: null };
  }
  const assessment = reason.match(/^(.+?) (Initial|Review) Assessment Submission Outstanding$/);
  if (assessment) {
    const name = ASSESSMENT_NAMES[assessment[1]] ?? assessment[1].toLowerCase();
    return {
      kind: 'assessment',
      title: `Submit the ${name} ${assessment[2].toLowerCase()} assessment`,
      detail: 'Not submitted yet',
      urgency: 'soon',
      dueAt: null,
    };
  }

  return { kind: 'assessment', title: reason, detail: '', urgency: 'soon', dueAt };
}

const reportTask = (
  clientId: string,
  programme: Programme,
  stage: 'initial' | 'review',
  fromAlert: { reason: string; described: Described } | null,
  allAssessmentsIn: boolean
): ClientTask => ({
  id: `${clientId}:${programme}:report:${stage}`,
  clientId,
  programme,
  kind: 'report',
  title: stage === 'initial' ? 'Generate the initial report' : 'Generate the review report',
  detail: allAssessmentsIn ? `All ${stage} assessments are in` : fromAlert?.described.detail ?? '',
  urgency: fromAlert ? fromAlert.described.urgency : 'soon',
  dueAt: fromAlert?.described.dueAt ?? null,
  alertReason: fromAlert?.reason,
});

function alertTasks(
  clientId: string,
  programme: Programme,
  reasons: string[],
  pending: { initial: boolean; review: boolean },
  start: Date | null,
  end: Date | null,
  now: Date
): ClientTask[] {
  const tasks: ClientTask[] = [];
  const reportAlerts: Record<'initial' | 'review', { reason: string; described: Described } | null> = { initial: null, review: null };
  reasons.forEach((reason) => {
    const described = describeAlertReason(reason, start, end, now);
    if (described.kind === 'report') {
      reportAlerts[reason.startsWith('Initial') ? 'initial' : 'review'] = { reason, described };
      return;
    }
    tasks.push({ id: `${clientId}:${programme}:alert:${reason}`, clientId, programme, alertReason: reason, ...described });
  });
  (['initial', 'review'] as const).forEach((stage) => {
    if (reportAlerts[stage] || pending[stage]) tasks.push(reportTask(clientId, programme, stage, reportAlerts[stage], pending[stage]));
  });
  return tasks;
}

function checkInTask(clientId: string, raw: RawClient, completed: Set<string>, now: Date): ClientTask | null {
  const first = firstSessionDate(raw);
  if (!first || !raw.active_programme || !(first > CHECK_IN_CUTOFF)) return null;
  const due = (['2_week', '10_week'] as const)
    .map((type) => ({ type, at: addWeeks(first, type === '2_week' ? 2 : 10) }))
    .find(({ type, at }) => isBefore(at, now) && !completed.has(`${clientId}_${type}`));
  if (!due) return null;
  return {
    id: `${clientId}:checkin:${due.type}`,
    clientId,
    programme: null,
    kind: 'checkin',
    title: due.type === '2_week' ? 'Log the 2-week check-in' : 'Log the 10-week check-in',
    detail: `Due ${fmtDay(due.at)}`,
    urgency: 'overdue',
    dueAt: due.at,
    checkInType: due.type,
  };
}

function successTask(clientId: string, raw: RawClient, latestByClient: Map<string, Date>, now: Date): ClientTask | null {
  if (raw.active_programme !== true) return null;
  const intervalMs = SUCCESS_UPDATE_INTERVAL_DAYS * DAY_MS;
  const sixWeeksAgo = now.getTime() - intervalMs;
  const latest = latestByClient.get(clientId) ?? null;
  const first = firstSessionDate(raw);
  const base = latest ?? first;
  if (!base || base.getTime() >= sixWeeksAgo) return null;
  return {
    id: `${clientId}:success`,
    clientId,
    programme: null,
    kind: 'success',
    title: latest ? 'Write a success update' : 'Write the first success update',
    awaitingFirst: !latest,
    detail: latest ? `Last update ${fmtDay(latest)}` : `No update since the first session on ${fmtDay(base)}`,
    urgency: 'overdue',
    dueAt: new Date(base.getTime() + intervalMs),
  };
}

/**
 * A renewal conversation is due when the client's current programme (Ignition of any year, Foundation or
 * Pre/Post Natal) ends in the next 1-14 days, by that programme's own end date.
 */
function renewalTask(clientId: string, raw: RawClient, now: Date): ClientTask | null {
  const programme = currentProgramme(raw);
  if (!programme?.end) return null;
  const { end } = programme;
  const daysLeft = Math.floor((end.getTime() - now.getTime()) / DAY_MS);
  if (daysLeft <= 0 || daysLeft > 14) return null;
  // Only Ignition names carry a PT total ("| 22 PT"); Foundation and Pre/Post Natal names don't, so no count is shown for them.
  const total = programme.family === 'ignition' ? programmePtTotal(raw.current_programme) : null;
  const used = (raw.visit_history ?? []).filter((visit) => visit.pricing_option === raw.current_programme).length;
  const left = total === null ? '' : ` · ${Math.max(0, total - used)} of ${total} PT left`;
  return {
    id: `${clientId}:renewal`,
    clientId,
    programme: programme.family,
    kind: 'renewal',
    title: 'Talk about renewal',
    detail: `${programme.label} ends ${format(end, 'EEE d MMM')}${left}`,
    urgency: daysLeft <= 7 ? 'soon' : 'upcoming',
    dueAt: end,
  };
}

/** Every open task for one client, from whichever sources have loaded. */
export function deriveClientTasks(client: LoadedClient, inputs: TaskInputs, now: Date): ClientTask[] {
  const { raw, clientId } = client;
  const tasks: ClientTask[] = [];

  // Alerts wait for the cleared lists so a cleared alert never flashes up; a failed list (null) shows them all.
  if (inputs.cleared !== undefined) {
    const cleared = inputs.cleared;
    const ignitionReasons = ignitionAlertReasons(raw, now, inputs.reports, inputs.submissions).filter(
      (reason) => !cleared?.ignition.has(reason)
    );
    const ignitionPending =
      inputs.reports && inputs.submissions ? ignitionPendingReports(raw, inputs.reports, inputs.submissions) : { initial: false, review: false };
    tasks.push(
      ...alertTasks(clientId, 'ignition', ignitionReasons, ignitionPending, toDate(raw.first_ignition_session_date), toDate(raw.ignition_expiration_date), now)
    );

    const foundation = foundationAlerts(raw, now, inputs.reports, inputs.submissions);
    const foundationReasons = foundation.reasons.filter((reason) => !cleared?.foundation.has(reason));
    tasks.push(
      ...alertTasks(
        clientId,
        'foundation',
        foundationReasons,
        { initial: foundation.pendingInitial, review: foundation.pendingReview },
        toDate(raw.first_foundation_session_date),
        toDate(raw.foundation_expiration_date),
        now
      )
    );
  }

  if (inputs.completedCheckIns) {
    const task = checkInTask(clientId, raw, inputs.completedCheckIns, now);
    if (task) tasks.push(task);
  }
  if (inputs.latestSuccess) {
    const task = successTask(clientId, raw, inputs.latestSuccess, now);
    if (task) tasks.push(task);
  }
  const renewal = renewalTask(clientId, raw, now);
  if (renewal) tasks.push(renewal);

  return sortTasks(tasks);
}

export const URGENCY_RANK: Record<Urgency, number> = { overdue: 0, soon: 1, upcoming: 2 };

const dueTime = (task: ClientTask) => (task.dueAt ? task.dueAt.getTime() : Number.POSITIVE_INFINITY);

/** Most urgent first; within a band, the one that fell due earliest. */
export function sortTasks(tasks: ClientTask[]): ClientTask[] {
  return [...tasks].sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] || dueTime(a) - dueTime(b) || a.title.localeCompare(b.title));
}

/** Worst band first; within a band, whoever has been waiting longest; then by name. */
export function sortWork(work: ClientWork[]): ClientWork[] {
  const firstDue = (item: ClientWork) =>
    Math.min(...item.tasks.filter((t) => t.urgency === item.urgency).map((t) => (t.dueAt ? t.dueAt.getTime() : Number.POSITIVE_INFINITY)));
  return [...work].sort(
    (a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] || firstDue(a) - firstDue(b) || a.client.name.localeCompare(b.client.name)
  );
}

/** "5d late", "Due 30 Oct" or "" for undated work. */
export function dueLabel(task: ClientTask, now: Date): string {
  if (!task.dueAt) return '';
  const days = Math.floor((now.getTime() - task.dueAt.getTime()) / DAY_MS);
  if (task.kind === 'renewal') {
    const left = Math.max(1, Math.ceil((task.dueAt.getTime() - now.getTime()) / DAY_MS));
    return left === 1 ? 'Ends tomorrow' : `Ends in ${left}d`;
  }
  if (days >= 1) return `${days}d late`;
  if (days === 0 && task.dueAt.getTime() <= now.getTime()) return 'Due today';
  return `Due ${fmtDay(task.dueAt)}`;
}

export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  report: 'Reports',
  assessment: 'Assessments',
  session: 'Sessions to book',
  checkin: 'Check-ins',
  success: 'Success updates',
  renewal: 'Renewals',
};
